import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { fetchProperties } from '@/store/slices/propertySlice';
import { PropertySearch } from '@/components/property/PropertySearch/PropertySearch';
import FeaturedProperties from '@/components/home/FeaturedProperties';
import PropertyGrid from '@/components/property/PropertyGrid/PropertyGrid';
import ContactForm from '@/components/property/ContactForm/ContactForm';
import CTASection from '@/components/home/CTASection';
import { HomeIcon, CheckIcon, DollarIcon, StarIcon, BuildingIcon, CastleIcon, FactoryIcon } from '@/components/common/Icons';

const Properties: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { properties, isLoading, error, pagination } = useSelector(
    (state: RootState) => state.properties
  );

  useEffect(() => {
    // Fetch properties when component mounts
    dispatch(fetchProperties({
      page: 1,
      limit: 12,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }));
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Search */}
      <PropertySearch />
      {/* Featured Properties Section */}
      <FeaturedProperties />
      {/* Property Portfolio Section */}
      <div className="w-full bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-8 lg:gap-12">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary">Property Portfolio</h2>
              <p className="text-sm md:text-base font-normal leading-[150%] text-black">
                Our comprehensive collection of premium properties across all price ranges and locations
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <HomeIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">{pagination.total || 0}</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Total Properties</div>
                <div className="text-xs text-secondary mt-1">Premium collection</div>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <CheckIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">{properties.length}</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Available Now</div>
                <div className="text-xs text-secondary mt-1">Ready to view</div>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <DollarIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">$2.5B+</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Total Value</div>
                <div className="text-xs text-secondary mt-1">Portfolio worth</div>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-tertiary">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <StarIcon />
                </div>
                <div className="text-xl md:text-2xl font-semibold text-secondary mb-1">4.9★</div>
                <div className="text-xs md:text-sm font-medium text-secondary">Client Rating</div>
                <div className="text-xs text-secondary mt-1">Based on reviews</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Find Your Perfect Home Section */}
      <div className="w-full bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-8 lg:gap-12">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary">
                Find Your Perfect Home
              </h2>
              <p className="text-sm md:text-base font-normal leading-[150%] text-black">
                Explore our diverse range of property types to find the perfect match for your lifestyle and needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-card-primary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <HomeIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Houses</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Spacious family homes with private yards and modern amenities</p>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-card-primary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 text-secondary bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <BuildingIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Apartments</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Modern city apartments with stunning views and premium facilities</p>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-card-primary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 text-secondary bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <CastleIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Luxury</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Exclusive estates with premium finishes and exceptional locations</p>
              </div>
              <div className="text-center p-4 md:p-5 border border-gray-200 bg-card-primary cursor-pointer hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 md:w-12 md:h-12 text-secondary bg-gray-200 flex items-center justify-center mx-auto mb-2">
                  <FactoryIcon />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-secondary mb-2">Commercial</h3>
                <p className="text-xs md:text-sm font-normal text-secondary leading-[150%]">Prime business spaces in strategic locations for investment opportunities</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Properties Section */}
      <div className="w-full bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] text-primary mb-2">All Properties</h2>
                {pagination.total > 0 && (
                  <p className="text-sm md:text-base font-normal leading-[150%] text-black">
                    Showing {properties.length} of {pagination.total} premium properties
                  </p>
                )}
              </div>
            </div>

            <PropertyGrid
              properties={properties}
              isLoading={isLoading}
              error={error}
              emptyMessage="No properties available at the moment. Please check back later."
            />

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-3 border-t border-gray-300">
                <div className="text-sm md:text-base font-medium leading-[150%]">
                  <span className="text-primary">Page {pagination.page}</span>
                  <span className="text-primary"> of {pagination.totalPages}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    className="p-1.5 md:p-2 border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={pagination.page === 1}
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M25.5 15C25.5 15.6213 24.9963 16.125 24.375 16.125L8.41812 16.125L14.6547 22.0641C15.1026 22.4947 15.1166 23.2069 14.6859 23.6548C14.2553 24.1026 13.5431 24.1166 13.0953 23.6859L4.84525 15.8109C4.62466 15.5988 4.5 15.306 4.5 15C4.5 14.694 4.62466 14.4012 4.84525 14.1891L13.0953 6.31406C13.5431 5.88342 14.2553 5.89739 14.6859 6.34525C15.1166 6.79312 15.1026 7.5053 14.6547 7.93594L8.41812 13.875L24.375 13.875C24.9963 13.875 25.5 14.3787 25.5 15Z" fill="#666666"/>
                    </svg>
                  </button>
                  <button 
                    className="p-1.5 md:p-2 border border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={pagination.page === pagination.totalPages}
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M4.5 15C4.5 14.3787 5.00368 13.875 5.625 13.875L21.5819 13.875L15.3453 7.93593C14.8974 7.50529 14.8834 6.79312 15.3141 6.34525C15.7447 5.89738 16.4569 5.88342 16.9047 6.31406L25.1547 14.1891C25.3753 14.4012 25.5 14.694 25.5 15C25.5 15.306 25.3753 15.5988 25.1547 15.8109L16.9047 23.6859C16.4569 24.1166 15.7447 24.1026 15.3141 23.6547C14.8834 23.2069 14.8974 22.4947 15.3453 22.0641L21.5819 16.125L5.625 16.125C5.00368 16.125 4.5 15.6213 4.5 15Z" fill="#333333"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <ContactForm />

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Properties;

